
import { Box, Button, CircularProgress } from "@mui/material";
import styles from "../../assets/styles/form.module.css";
import { useState } from "react";
import { PinnacleTextField } from "../../components/PinnacleTextField";
import { handleApiError } from "../../utils/errorHandler";
import { BTN_UPDATE, E_CONFIRM_PASSWORD, E_ENTER_PASSWORD, E_PASSWORD_NOT_MATCHED } from "../../utils/constants";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { blue, grey } from "@mui/material/colors";
import PasswordAdornment from '../../components/common/PasswordAdornment/PasswordAdornment';
import { changePassword } from '../../api/services/auth';

interface AccountChangePasswordProps {
    setIsOpen: (isOpen: boolean) => void;
}
export default function AccountChangePassword(props: AccountChangePasswordProps) {
    const { setIsOpen } = props;

    const [formDisabled, setFormDisabled] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Old pasword
    const [oldPassword, setOldPassword] = useState<string>('');
    const [oldPasswordTouched, setOldPasswordTouched] = useState<boolean>(false);
    const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
    const isOldPasswordValid = oldPassword.length > 0;

    const handleToggleOldPassword = () => setShowOldPassword((prev) => !prev);

    const handleOldPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setOldPassword(e.target.value);
        if (!oldPasswordTouched) setOldPasswordTouched(true);
        if (errorMessage) setErrorMessage(null);
    };

    // New password
    const [password, setPassword] = useState<string>('');
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const isPasswordValid = password.length > 0;

    const handleTogglePassword = () => setShowPassword((prev) => !prev);

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        if (!passwordTouched) setPasswordTouched(true);
        if (errorMessage) setErrorMessage(null);
    };

    // Confirm new password
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const isConfirmPasswordValid = confirmPassword.length > 0;
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [confirmPasswordTouched, setConfirmPasswordTouched] = useState<boolean>(false);

    const handleToggleConfirmPassword = () => setShowConfirmPassword((prev) => !prev);

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value);
        if (!confirmPasswordTouched) setConfirmPasswordTouched(true);
        if (errorMessage) setErrorMessage(null);
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!oldPassword || !password || !confirmPassword) {
            setFormDisabled(false);
            return;
        }

        setFormDisabled(true);
        try {
            const changePasswordResponse = await changePassword({
                oldPassword,
                newPassword: password,
            });
            pushNotification({
                details: changePasswordResponse.message,
                type: NotificationTypes.Success,
            });
            setFormDisabled(true);
            setIsOpen(false);
        } catch (error) {
            const { errorMessage } = handleApiError(error);
            pushNotification({
                details: errorMessage,
                type: NotificationTypes.Error,
            });
        } finally {
            setFormDisabled(false);
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit} noValidate>
                <Box className={styles["form-panel-box"]}>

                    {/* OLD PASSWORD */}
                    <PinnacleTextField
                        error={oldPasswordTouched && (!isOldPasswordValid || !!errorMessage)}
                        color={oldPasswordTouched && (!isOldPasswordValid || !!errorMessage) ? 'error' : 'primary'}
                        helperText={
                            oldPasswordTouched && !isOldPasswordValid
                                ? E_ENTER_PASSWORD
                                : errorMessage
                        }
                        id="oldPassword"
                        label="Old Password"
                        variant="outlined"
                        onChange={handleOldPasswordChange}
                        onBlur={() => setOldPasswordTouched(true)}
                        value={oldPassword}
                        type={showOldPassword ? 'text' : 'password'}
                        autoComplete="old-password"
                        name="old-password"
                        disabled={formDisabled}
                        slotProps={{
                            input: {
                                endAdornment: <PasswordAdornment show={showOldPassword} toggleShow={handleToggleOldPassword} />,
                            },
                        }}
                    />

                    {/* NEW PASSWORD */}
                    <PinnacleTextField
                        error={passwordTouched && (!isPasswordValid || !!errorMessage)}
                        color={passwordTouched && (!isPasswordValid || !!errorMessage) ? 'error' : 'primary'}
                        helperText={
                            passwordTouched && !isPasswordValid
                                ? E_ENTER_PASSWORD
                                : errorMessage
                        }
                        id="newPassword"
                        label="New Password"
                        variant="outlined"
                        onChange={handlePasswordChange}
                        onBlur={() => setPasswordTouched(true)}
                        value={password}
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        name="password"
                        disabled={formDisabled}
                        slotProps={{
                            input: {
                                endAdornment: <PasswordAdornment show={showPassword} toggleShow={handleTogglePassword} />,
                            },
                        }}
                    />

                    {/* CONFIRM PASSWORD */}
                    <PinnacleTextField
                        error={
                            (confirmPasswordTouched && (!isConfirmPasswordValid || !!errorMessage)) ||
                            password !== confirmPassword
                        }
                        color={
                            ((confirmPasswordTouched && (!isConfirmPasswordValid || !!errorMessage)) ||
                                password !== confirmPassword)
                                ? 'error'
                                : 'primary'
                        }
                        helperText={
                            (confirmPasswordTouched && !isConfirmPasswordValid)
                                ? E_CONFIRM_PASSWORD
                                : (password !== confirmPassword)
                                    ? E_PASSWORD_NOT_MATCHED
                                    : errorMessage
                        }
                        id="confirmNewPassword"
                        label="Confirm New Password"
                        variant="outlined"
                        onChange={handleConfirmPasswordChange}
                        onBlur={() => setConfirmPasswordTouched(true)}
                        value={confirmPassword}
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="current-confirm-password"
                        name="confirm-password"
                        disabled={formDisabled}
                        slotProps={{
                            input: {
                                endAdornment: <PasswordAdornment show={showConfirmPassword} toggleShow={handleToggleConfirmPassword} />,
                            },
                        }}
                    />

                </Box>
                <Box sx={{ mt: 2 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={!isPasswordValid || !isConfirmPasswordValid || formDisabled}
                        sx={{
                            mb: 1.5,
                            '&.Mui-disabled': {
                                backgroundColor: grey[800],
                                color: grey[400],
                                opacity: 1,
                            },
                        }}
                    >
                        {formDisabled ? (
                            <CircularProgress size={24} sx={{ color: blue[300] }} />
                        ) : (
                            BTN_UPDATE
                        )}
                    </Button>
                </Box>
            </form>
        </>
    );
}